import React, { useState } from 'react';
import { Box, Card, CardContent, IconButton, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Grid, FormControlLabel, Checkbox, Typography, InputAdornment, Divider, Button, Stack } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EditIcon from '@mui/icons-material/Edit';

const Question = React.memo(function Question({
  question,
  index,
  handleQuestionChange,
  removeQuestion,
  addOption,
  removeOption,
  toggleQuestionType,
  moveQuestionUp,
  moveQuestionDown,
  totalQuestions
}) {
  const [isEditMode, setIsEditMode] = useState(false);

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
  };

  // Get a summary of question configuration for the collapsed view
  const getQuestionSummary = () => {
    let summary = `Type: ${question.questionType.replace('-', ' ')}`;
    
    if (question.questionType === 'multiple-choice') {
      summary += ` | Options: ${question.responseOptions.length}`;
      if (question.selectMin || question.selectMax) {
        summary += ` | Select: `;
        if (question.selectMin) summary += `min ${question.selectMin}`;
        if (question.selectMin && question.selectMax) summary += ', ';
        if (question.selectMax) summary += `max ${question.selectMax}`;
      }
    } else if (question.questionType === 'open-ended') {
      summary += ` | Max length: ${question.maxLength || 300}`;
    } else if (question.questionType === 'open-list') {
      if (question.selectMin || question.selectMax) {
        summary += ` | Select: `;
        if (question.selectMin) summary += `min ${question.selectMin}`;
        if (question.selectMin && question.selectMax) summary += ', ';
        if (question.selectMax) summary += `max ${question.selectMax}`;
      }
    }
    
    return summary;
  };

  return (
    <Card variant="outlined" sx={{ mb: 2, borderRadius: 1, boxShadow: 1 }}>
      <CardContent>
        {!isEditMode ? (
          // Collapsed view - only show question text and minimal controls
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* Vertical arrow controls */}
            <Stack direction="column" spacing={0} sx={{ mr: 1 }}>
              <Tooltip title="Move Question Up">
                <span>
                  <IconButton 
                    onClick={() => moveQuestionUp(index)} 
                    disabled={index === 0}
                    sx={{ 
                      color: index === 0 ? 'grey.400' : 'primary.main',
                      padding: '4px'
                    }}
                    size="small"
                  >
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move Question Down">
                <span>
                  <IconButton 
                    onClick={() => moveQuestionDown(index)} 
                    disabled={index === totalQuestions - 1}
                    sx={{ 
                      color: index === totalQuestions - 1 ? 'grey.400' : 'primary.main',
                      padding: '4px'
                    }}
                    size="small"
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
            
            {/* Question content */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                {`Q${index + 1}: ${question.questionText || 'No question text'}`}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {getQuestionSummary()}
              </Typography>
            </Box>
            
            {/* Action buttons */}
            <Box>
              <Tooltip title="Edit Question">
                <IconButton onClick={toggleEditMode} color="primary">
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove Question">
                <IconButton onClick={() => removeQuestion(index)}>
                  <DeleteOutlineIcon color="error" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ) : (
          // Expanded view - show all editing options
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <TextField
                fullWidth
                label={`Question ${index + 1}`}
                value={question.questionText}
                onChange={(e) => handleQuestionChange(index, 'questionText', e.target.value)}
                sx={{ mb: 2 }}
              />
              <Box>
                <Tooltip title="Move Question Up">
                  <span>
                    <IconButton 
                      onClick={() => moveQuestionUp(index)} 
                      disabled={index === 0}
                      sx={{ color: index === 0 ? 'grey.400' : 'primary.main' }}
                    >
                      <ArrowUpwardIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move Question Down">
                  <span>
                    <IconButton 
                      onClick={() => moveQuestionDown(index)} 
                      disabled={index === totalQuestions - 1}
                      sx={{ color: index === totalQuestions - 1 ? 'grey.400' : 'primary.main' }}
                    >
                      <ArrowDownwardIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Remove Question">
                  <IconButton onClick={() => removeQuestion(index)}>
                    <DeleteOutlineIcon color="error" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <ToggleButtonGroup
              size='small'
              value={question.questionType}
              exclusive
              onChange={(event, newType) => toggleQuestionType(index, newType)}
              sx={{
                mb: 3,
                '& .MuiToggleButton-root': {
                  color: '#2d2d2d',
                  backgroundColor: 'var(--primary-color)',
                  '&.Mui-selected': {
                    backgroundColor: 'var(--tertiary-color)',
                    color: 'var(--background-color)',
                  },
                  '&:hover': {
                    backgroundColor: 'var(--secondary-color)',
                  },
                }
              }}
            >
              <ToggleButton value="open-ended">Open-Ended</ToggleButton>
              <ToggleButton value="multiple-choice">Multiple Choice</ToggleButton>
              <ToggleButton value="open-list">Open List</ToggleButton>
            </ToggleButtonGroup>
            
            {question.questionType === 'open-ended' && (
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Max Length"
                  type="number"
                  value={question.maxLength || 300}
                  onChange={(e) => handleQuestionChange(index, 'maxLength', e.target.value)}
                  InputProps={{ inputProps: { min: 0 } }}
                />
              </Box>
            )}

            {question.questionType === 'open-list' && (
              <Box sx={{ mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      label="Select Min"
                      type="number"
                      value={question.selectMin || ''}
                      onChange={(e) => handleQuestionChange(index, 'selectMin', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      label="Select Max"
                      type="number"
                      value={question.selectMax || ''}
                      onChange={(e) => handleQuestionChange(index, 'selectMax', e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}


            {question.questionType === 'multiple-choice' && (
              <>
                <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth
                        label="Select Max"
                        type="number"
                        value={question.selectMax || ''}
                        onChange={(e) => handleQuestionChange(index, 'selectMax', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth
                        label="Select Min"
                        type="number"
                        value={question.selectMin || ''}
                        onChange={(e) => handleQuestionChange(index, 'selectMin', e.target.value)}
                      />
                    </Grid>
                  </Grid>

                <Divider sx={{ mb: 2, mt: 2 }} />

                {question.responseOptions.map((option, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <TextField
                      fullWidth
                      label={`Option ${i + 1}`}
                      value={option}
                      onChange={(e) => {
                        const updatedOptions = [...question.responseOptions];
                        updatedOptions[i] = e.target.value;
                        handleQuestionChange(index, 'responseOptions', updatedOptions);
                      }}
                      sx={{ mr: 1, mt: 1 }}
                    />
                    <Tooltip title="Remove Option">
                      <IconButton onClick={() => removeOption(index, i)}>
                        <RemoveCircleOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}

                <Box sx={{ mt: 2 }}>
                  <Tooltip title="Add Option">
                    <IconButton onClick={() => addOption(index)}>
                      <AddCircleOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </Box>

                <FormControlLabel
                    sx={{ ml: 0 }}
                    control={
                      <Checkbox
                        checked={question.hasFixedPercentages}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          handleQuestionChange(index, "hasFixedPercentages", checked);
                  
                          if (
                            checked &&
                            (!question.responsePercentages || question.responsePercentages.length !== question.responseOptions.length)
                          ) {
                            handleQuestionChange(index, "responsePercentages", question.responseOptions.map(() => 0));
                          }
                        }}
                      />
                    }
                    label="Fix Response Percentages"
                  />

                  {question.hasFixedPercentages && question.responseOptions.map((option, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography>{option}:</Typography>
                      <TextField
                        type="number"
                        value={question.responsePercentages?.[i] || 0}
                        onChange={(e) => {
                          const updatedPercentages = [...(question.responsePercentages || [])];
                          updatedPercentages[i] = Number(e.target.value);
                          handleQuestionChange(index, 'responsePercentages', updatedPercentages);
                        }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        error={question.responsePercentages?.map(Number).reduce((a, b) => a + b, 0) !== 100}
                        helperText={question.responsePercentages?.reduce((a, b) => a + b, 0) !== 100 ? 
                          "Percentages must sum to 100" : ""}
                      />
                    </Box>
                  ))}
              </>
            )}
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                variant="contained"
                onClick={toggleEditMode} 
                color="primary"
              >
                Done
              </Button>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
});

export default Question;

